import { ComponentRef, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '~/modules/elements/akui/button';
import ConfirmModal from '~/modules/elements/akui/confirm-modal';
import { Icon } from '~/modules/elements/akui/icon';
import { Menu } from '~/modules/elements/akui/menu';
import InfoGlyph from '~/modules/elements/info-glyph';
import { Input } from '~/modules/elements/input';
import Logo from '~/modules/elements/logo';
import Modal from '~/modules/elements/modal';
import events from '~/modules/game-events/game-events';
import { useEventEffect } from '~/modules/game-events/hooks';
import useKeyboardNav from '~/modules/hooks/use-keyboard-nav';
import RemoteMicServer from '~/modules/remote-mic/network/server';
import UserProfiles, { ProfileWriteError, UserProfile } from '~/modules/user-profiles/profiles';
import ProfileAvatar from '~/routes/profiles/profile-avatar';
import WebcamCapture from '~/routes/profiles/webcam-capture';

interface Props {
  open: boolean;
  /** The profile being edited, or `null` to create a new one. */
  profile: UserProfile | null;
  onClose: () => void;
  /**
   * The profile a create just made, for callers that opened the form in order to use it - the mic
   * setup screen puts it straight onto the slot that asked. Never called for an edit. Fires before
   * `onClose`, so a caller can act and close in one pass.
   */
  onCreated?: (created: UserProfile) => void;
  /** `nested` when the form is opened from inside another modal - see `Modal`. */
  level?: 'base' | 'nested';
}

/**
 * `MenuContainer` pins its own max-width at three breakpoints, so widening - or here narrowing - it
 * means overriding every one of them, the same way the profile screens do.
 */
const FORM_CARD = 'sm:max-w-[min(94vw,34rem)] lg:max-w-[min(94vw,34rem)] 2xl:max-w-[min(94vw,34rem)]';

export default function ProfileFormModal({ open, profile, onClose, onCreated, level }: Props) {
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState<string | undefined>(undefined);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState('');
  const nameRef = useRef<ComponentRef<typeof Input>>(null);

  useEffect(() => {
    if (!open) return;
    setName(profile?.name ?? '');
    setAvatar(profile?.avatar);
    setCapturing(false);
    setError('');
  }, [open, profile]);

  // A picture taken on a phone. It arrives already cropped and downscaled to the same square the
  // webcam produces, so there is nothing to do but put it on - and the television's own capture
  // steps aside, since the photo it was opened to take has just arrived from somewhere else.
  useEventEffect(events.remoteProfilePhotoTaken, (dataUrl) => {
    if (!open) return;
    setAvatar(dataUrl);
    setCapturing(false);
  });

  /**
   * A refusal to save. The phone cannot see the `Menu.HelpText` this writes - it mirrors controls,
   * and the reason a press did nothing is not one - so the string is flashed through `phone-toast`
   * as well. To every connected phone rather than to whoever pressed: one layout is published to all
   * of them, so every phone showing this form is a phone the refusal is about.
   */
  const refuse = (message: string) => {
    setError(message);
    RemoteMicServer.callAllClients('flashMessage', message);
  };

  const save = () => {
    const trimmed = name.trim();
    if (trimmed === '') {
      refuse('A profile needs a name.');
      return;
    }
    // The name is how plays are attributed, so two profiles sharing one would split a single
    // person's history between them.
    if (UserProfiles.isNameTaken(trimmed, profile?.id)) {
      refuse(`There is already a profile called ${trimmed}.`);
      return;
    }

    try {
      if (profile) {
        UserProfiles.update(profile.id, { name: trimmed, avatar });
      } else {
        // Created on its own line: `onCreated?.(UserProfiles.create(...))` short-circuits the whole
        // call expression when there is no callback, argument included, so no profile was created
        // at all for every caller that does not pass one.
        const created = UserProfiles.create(trimmed, avatar);
        onCreated?.(created);
      }
    } catch (e) {
      refuse(e instanceof ProfileWriteError ? e.message : 'Could not save the profile.');
      return;
    }

    onClose();
  };

  // Memoised, so the layout is republished when what it says changes rather than on every render: a
  // fresh object each time would push a new `HelpEntry` to every phone for any re-render at all.
  const subject = name.trim();
  const taken = avatar !== undefined;
  const photo = useMemo(() => ({ subject, taken }), [subject, taken]);

  // Until this existed the form published no layout at all, so the phone kept whichever screen had
  // registered one before it - Setup microphones, or whatever the mic setup itself was opened over -
  // and a tap acted on controls nobody could see. `exclusive` is the actual fix: it pauses every
  // other navigation while the form is up, exactly as `confirm-modal.tsx` does for the delete
  // confirmation inside this same file, which is why "Delete Nathan?" already reached the phone and
  // "New singer" never did.
  const { register } = useKeyboardNav({
    enabled: open,
    exclusive: true,
    onBackspace: onClose,
    title: profile ? 'Edit profile' : 'New singer',
    // Named now that a primary only paints: it used to be promoted for being the first non-back
    // button, which was right by accident and stopped being true the moment the photo row moved.
    primary: 'save-profile',
    // The camera the phone has, offered as its own button rather than as a mirror of the
    // television's: there is nothing for the television to do until a picture comes back.
    photo,
  });

  return (
    <Modal open={open} onClose={onClose} withPortal level={level}>
      {/* The wordmark sits where a page's own does, so the top of the screen does not change when
          the form opens over Singers. Not when it is nested: the mic setup screen it opens over has
          no wordmark, and adding one there would be the same jump in reverse. The column is
          `pointer-events-none` so it does not swallow the backdrop clicks that close the form. */}
      <div className="pointer-events-none flex min-h-dvh flex-col items-center">
        {level !== 'nested' && (
          <div className="mobile:text-5xl pt-8 text-6xl">
            <Logo />
          </div>
        )}
        <Menu
          modal
          spacing="tight"
          className={`${FORM_CARD} my-auto gap-[1.25rem] p-[1.75rem]`}
          data-test="profile-form">
          <span className="stroke-text typography text-active text-center text-[2.125rem] font-bold">
            {profile ? 'Edit profile' : 'New singer'}
          </span>
          {capturing ? (
            <WebcamCapture
              onCapture={(dataUrl) => {
                setAvatar(dataUrl);
                setCapturing(false);
              }}
              onCancel={() => setCapturing(false)}
            />
          ) : (
            // The photo beside the name rather than stacked above it: a 30rem column that spent its
            // first third on an avatar nobody has taken yet made the field look like an afterthought.
            <div className="flex flex-wrap items-start gap-[1.25rem]">
              <div className="flex shrink-0 flex-col items-center gap-2.5">
                <ProfileAvatar profile={{ name, avatar }} className="size-[7rem] text-2xl" />
                {/* Screen-only, and not because a phone could not be told about them: these two drive
                    the TELEVISION's webcam, which is the one camera a phone cannot reach. The phone
                    offers its own instead (see the `photo` entry above), so each device offers the
                    camera it actually has rather than a remote shutter for somebody else's. */}
                <Button
                  size="small"
                  className="h-[2.75rem] w-[7rem] px-0 text-base"
                  fullWidth={false}
                  leftIcon={<Icon icon="ic:baseline-photo-camera" />}
                  {...register('add-photo', () => setCapturing(true), avatar ? 'Retake' : 'Photo', false, {
                    hideOnRemote: true,
                  })}
                  data-test="add-photo">
                  {avatar ? 'Retake' : 'Photo'}
                </Button>
                {avatar && (
                  <Button
                    size="small"
                    className="h-[2.75rem] w-[7rem] px-0 text-base"
                    {...register('remove-photo', () => setAvatar(undefined), 'Remove', false, {
                      hideOnRemote: true,
                    })}
                    data-test="remove-photo">
                    Remove
                  </Button>
                )}
              </div>

              <div className="flex min-w-[min(100%,14rem)] flex-1 flex-col gap-1.5">
                <span className="flex items-center gap-2 text-[0.9375rem] tracking-[0.08em] text-white/45 uppercase">
                  Name
                  <InfoGlyph text="Scores are matched to a profile by this name, so it is what the game records when this player sings." />
                </span>
                <Input
                  ref={nameRef}
                  {...register('profile-name', () => nameRef.current?.element?.focus(), 'Name', true, {
                    control: { type: 'text', label: 'Name', value: name, placeholder: 'Name' },
                    onValueChange: setName,
                  })}
                  label={null}
                  value={name}
                  onChange={setName}
                  size="small"
                  className="h-[3.875rem] text-[1.75rem] font-bold"
                  data-test="profile-name"
                />
              </div>
            </div>
          )}
          {error !== '' && (
            <Menu.HelpText className="text-red-400" data-test="profile-error">
              {error}
            </Menu.HelpText>
          )}
          {/* Save leads through width, not colour: the name field holds the keyboard, and an
              accent-filled Save beside it would claim the same Enter. */}
          <Menu.ButtonGroup className="flex-wrap gap-3">
            <Button
              size="small"
              className="h-[3.75rem] flex-[2_1_10rem]"
              {...register('save-profile', save, 'Save', false, { control: { type: 'button', label: 'Save' } })}
              data-test="save-profile">
              Save
            </Button>
            <Button
              size="small"
              className="h-[3.75rem] flex-[1_1_7rem]"
              {...register('cancel-profile', onClose, 'Cancel', false, {
                // Leading arrow rather than the forward one every other button gets: this is the way
                // out of the form, and the only one the phone has - it has no Escape key.
                control: { type: 'button', label: 'Cancel', variant: 'back' },
              })}
              data-test="cancel-profile">
              Cancel
            </Button>
          </Menu.ButtonGroup>
          {profile && (
            <ConfirmModal
              title={`Delete ${profile.name}?`}
              description="The songs sung under this name are kept - only the profile goes. Creating it again picks the same history back up."
              onConfirm={() => {
                try {
                  UserProfiles.remove(profile.id);
                } catch (e) {
                  setError(e instanceof ProfileWriteError ? e.message : 'Could not delete the profile.');
                  return;
                }
                onClose();
              }}
              dataTestPrefix="delete-profile-confirm"
              cancelButton={<ConfirmModal.CancelButton name="keep-profile">Keep it</ConfirmModal.CancelButton>}
              confirmButton={<ConfirmModal.ConfirmButton name="confirm-delete">Delete</ConfirmModal.ConfirmButton>}>
              {(openDeleteConfirm) => (
                <Button
                  size="small"
                  {...register('delete-profile', openDeleteConfirm, 'Delete profile', false, {
                    control: { type: 'button', label: 'Delete profile', variant: 'destructive' },
                  })}
                  data-test="delete-profile">
                  Delete profile
                </Button>
              )}
            </ConfirmModal>
          )}
        </Menu>
      </div>
    </Modal>
  );
}
